import type { MouseEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Package } from 'lucide-react'
import type { WarehouseItem, WarehouseLot } from '@/api/warehouse'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { StatusPill } from '@/components/status-pill'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn, formatDateOnly, formatMoney } from '@/lib/utils'
import { casesNote, conditionLabel, conditionTone, itemLink, locationLabel, netRange, quantityLabel } from './model'

// A click on any of these inside a row is theirs, not the row's.
const OWN_CLICK = 'a, button, input, select, textarea, label, [role="button"]'

/** How many places a row lists before "+N more". */
const PLACES_SHOWN = 2

export function ConditionPill({ stock }: { stock: Pick<WarehouseLot, 'condition' | 'used_grade'> }) {
  return <StatusPill tone={conditionTone(stock.condition)}>{conditionLabel(stock.condition, stock.used_grade)}</StatusPill>
}

function QuantityFacts({ item, align = 'left' }: { item: WarehouseItem; align?: 'left' | 'right' }) {
  const perCase = item.product.units_per_case
  const note = casesNote(item.quantity_on_hand, perCase)
  const reserved = item.quantity_reserved ? `${item.quantity_reserved} reserved` : null
  return (
    <div className={cn('shrink-0 whitespace-nowrap', align === 'right' && 'text-right')}>
      <p className="text-sm font-semibold tabular-nums">{quantityLabel(item.quantity_on_hand, perCase)}</p>
      <p className="text-[11px] text-muted-foreground tabular-nums">
        {[note, reserved].filter(Boolean).join(' · ') || (item.quantity_on_hand ? 'on hand' : 'sold out')}
      </p>
    </div>
  )
}

/** The first few places, each with its count when there is more than one, then "+N more". */
export function PlacesList({
  lots,
  showWarehouse = false,
  className,
}: {
  lots: WarehouseLot[]
  showWarehouse?: boolean
  className?: string
}) {
  const shown = lots.slice(0, PLACES_SHOWN)
  const more = lots.length - shown.length
  return (
    <ul className={cn('space-y-0.5 text-sm', className)}>
      {shown.map((lot) => (
        <li key={lot.uuid} className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate">
            {locationLabel(lot) ?? <span className="text-muted-foreground">No bay</span>}
            {showWarehouse && <span className="text-muted-foreground"> · {lot.warehouse.name}</span>}
          </span>
          {lots.length > 1 && (
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">× {lot.quantity_on_hand}</span>
          )}
        </li>
      ))}
      {more > 0 && <li className="text-xs text-muted-foreground">+{more} more</li>}
    </ul>
  )
}

function ProductCell({ item }: { item: WarehouseItem }) {
  const { product } = item
  return (
    <POLineItemProductCell
      model={product.model}
      description={product.store_title}
      productUuid={product.uuid}
      imageUrl={product.image_url}
      manufacturerName={product.manufacturer.short_name || product.manufacturer.name}
      manufacturerUuid={product.manufacturer.uuid}
      modelContent={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <Link {...itemLink(item)} className="break-words text-sm font-medium text-primary hover:underline">
            {product.model}
          </Link>
          <ConditionPill stock={item} />
        </span>
      }
      compact
      collapseLongDescription
      collapsedDescriptionLines={1}
    />
  )
}

export function StockTableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 rounded-lg border p-3">
          <Skeleton className="size-10 rounded-md" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-64" />
          </div>
          <Skeleton className="h-5 w-12" />
        </div>
      ))}
    </div>
  )
}

/** What we hold, one row per product and condition, in the Projects/PO list style. */
export function StockTable({ items, showWarehouse }: { items: WarehouseItem[]; showWarehouse: boolean }) {
  const navigate = useNavigate()

  const openRow = (event: MouseEvent<HTMLTableRowElement>, item: WarehouseItem) => {
    const target = event.target as Element
    if (!event.currentTarget.contains(target) || target.closest(OWN_CLICK)) return
    if (window.getSelection()?.toString()) return
    const link = itemLink(item)
    if (event.metaKey || event.ctrlKey) {
      const query = new URLSearchParams({ condition: item.condition, ...(item.used_grade ? { grade: item.used_grade } : {}) })
      window.open(`/warehouse/item/${item.product.uuid}?${query}`, '_blank', 'noopener')
      return
    }
    void navigate(link)
  }

  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Quantity</TableHead>
              <TableHead>Locations</TableHead>
              <TableHead>Received</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const net = netRange(item.lots, formatMoney)
              return (
                <TableRow
                  key={`${item.product.uuid}-${item.condition}-${item.used_grade ?? ''}`}
                  className="cursor-pointer"
                  onClick={(event) => openRow(event, item)}
                >
                  <TableCell className="min-w-0 max-w-[440px] whitespace-normal">
                    <ProductCell item={item} />
                  </TableCell>
                  <TableCell>
                    <QuantityFacts item={item} />
                  </TableCell>
                  <TableCell className="max-w-[260px] whitespace-normal">
                    <PlacesList lots={item.lots} showWarehouse={showWarehouse} />
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{formatDateOnly(item.last_received_at)}</p>
                    {net && <p className="text-xs text-muted-foreground tabular-nums">{net} Net each</p>}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2 md:grid md:grid-cols-2 md:gap-3 md:space-y-0 lg:hidden">
        {items.map((item) => (
          <Link
            key={`${item.product.uuid}-${item.condition}-${item.used_grade ?? ''}`}
            {...itemLink(item)}
            className="flex gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
          >
            <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-white">
              {item.product.image_url ? (
                <img src={item.product.image_url} alt="" className="size-full object-contain p-0.5" />
              ) : (
                <Package className="size-5 text-muted-foreground/40" aria-hidden />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    <p className="break-words text-sm font-medium text-primary">{item.product.model}</p>
                    <ConditionPill stock={item} />
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.product.store_title ?? item.product.manufacturer.name}
                  </p>
                </div>
                <QuantityFacts item={item} align="right" />
              </div>
              <PlacesList lots={item.lots} showWarehouse={showWarehouse} className="mt-1.5 text-xs text-muted-foreground" />
            </div>
          </Link>
        ))}
      </div>
    </>
  )
}
